import React from 'react';
import dataOpsLogo from 'assets/logos/dataops/dataopslive-infinity-blues-on-white.svg';
import { motion, MotionProps } from 'motion/react';

interface DataOpsIconProps extends Omit<MotionProps, 'src'> {
  /**
   * Optional CSS class name(s) to apply to the icon
   */
  className?: string;
  /**
   * Size of the icon (applies to both width and height)
   * @default 'h-8 w-8'
   */
  size?: string;
  /**
   * Whether the icon should be hidden from screen readers
   * @default true
   */
  ariaHidden?: boolean;
  /**
   * Alt text for the icon (for accessibility when ariaHidden is false)
   */
  alt?: string;
}

/**
 * DataOpsIcon Component
 *
 * A reusable component that displays the DataOps.live infinity logo.
 * The icon SVG is served from the assets directory.
 * Supports all Motion props for animations and interactions.
 *
 * @component
 * @example
 * ```tsx
 * // Default usage
 * <DataOpsIcon />
 *
 * // Custom size and styling
 * <DataOpsIcon size="h-12 w-12" className="rounded-lg bg-white p-2" />
 *
 * // With accessibility
 * <DataOpsIcon ariaHidden={false} alt="DataOps.live logo" />
 *
 * // With motion props
 * <DataOpsIcon
 *   initial={{ opacity: 0, y: -20 }}
 *   animate={{ opacity: 1, y: 0 }}
 *   transition={{ duration: 0.5 }}
 *   whileHover={{ scale: 1.05 }}
 * />
 * ```
 *
 * @param props - Component props (including all MotionProps)
 * @returns DataOps logo image element
 */
const DataOpsIcon: React.FC<DataOpsIconProps> = ({
  className = '',
  size = 'h-5',
  ariaHidden = true,
  alt = 'DataOps.live',
  ...motionProps
}) => {
  return (
    <motion.img
      src={dataOpsLogo}
      className={`${size} ${className}`.trim()}
      aria-hidden={ariaHidden}
      alt={ariaHidden ? '' : alt}
      {...motionProps}
    />
  );
};

export default DataOpsIcon;
